"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldAlert, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function SetupChecks() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="usedbullsetupchecks" data-nav="light" className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative bg-card text-card-foreground border border-border rounded-none shadow-xs overflow-hidden">
          {/* Top Industrial Specification Plate Strip */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/60 px-4 py-2.5 sm:px-6">
            <div className="flex items-center gap-2">
              <span className="inline-block size-2 bg-primary rounded-none" aria-hidden="true" />
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Spec Ref: SEC-04 // Rigging & Verification
              </span>
            </div>
            <div className="flex items-center gap-3 font-mono text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <ShieldAlert className="size-3.5 text-primary" aria-hidden="true" />
                Mandatory Check
              </span>
            </div>
          </div>

          {/* Main Rating Plate Body */}
          <div className="p-6 sm:p-10 md:p-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="hidden sm:flex size-10 shrink-0 items-center justify-center bg-muted border border-border text-foreground rounded-none">
                <Wrench className="size-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <span className="block font-mono text-xs uppercase tracking-widest text-primary mb-1">
                  Field Bulletin
                </span>
                <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight text-foreground">
                  Extra Setup Checks for a Used Mechanical Bull
                </h2>
              </div>
            </div>

            {/* Dictated Guidance Paragraphs */}
            <div className="space-y-4 font-sans text-base sm:text-lg leading-relaxed text-muted-foreground border-l-2 border-primary/40 pl-4 sm:pl-6 my-8">
              <p>
                A used mechanical bull may arrive with equipment from different stages of its working life. For that reason, confirm exactly which controller, inflatable, blower, cables and accessories come with the individual machine.
              </p>
              <p>
                Also review any available condition information, known repairs and replacement components before setup. If documentation is missing, do not assume that another machine&apos;s instructions apply.
              </p>
            </div>

            {/* Machinery Footnote & Action Link */}
            <div className="pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="font-mono text-xs text-muted-foreground">
                <span className="font-semibold text-foreground uppercase">Inventory Notice:</span> Used equipment is cataloged per serial registry.
              </div>

              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-11 px-6 font-serif uppercase tracking-wider font-semibold text-base shrink-0 shadow-xs transition-[background-color,box-shadow]">
                <Link to="/UsedMechanicalBullsPage" className="inline-flex items-center gap-2">
                  <span>View Used Mechanical Bulls</span>
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Hairline Industrial Fastener Accents */}
          <div className="absolute top-2 left-2 size-1.5 border-t border-l border-muted-foreground/30 pointer-events-none" aria-hidden="true" />
          <div className="absolute top-2 right-2 size-1.5 border-t border-r border-muted-foreground/30 pointer-events-none" aria-hidden="true" />
          <div className="absolute bottom-2 left-2 size-1.5 border-b border-l border-muted-foreground/30 pointer-events-none" aria-hidden="true" />
          <div className="absolute bottom-2 right-2 size-1.5 border-b border-r border-muted-foreground/30 pointer-events-none" aria-hidden="true" />
        </motion.div>
      </div>
    </section>;
}